import { useStore } from '@/store/StoreContext';
import { Card, SectionTitle, EmptyState } from '@/components/ui';
import { UserCheck, Award, Calendar, CheckSquare, BookMarked, TrendingUp, LogOut } from 'lucide-react';

export function AgentPortalView() {
  const { classes } = useStore();
  const loggedInAgentId = localStorage.getItem('uisce-agent-id');

  // Search across ALL cohorts to find the logged-in agent
  const allAgents = classes.flatMap((c) => c.agents);
  const agent = allAgents.find((a) => a.id === loggedInAgentId) || allAgents[0];

  const handleSignOut = () => {
    localStorage.removeItem('uisce-user-role');
    localStorage.removeItem('uisce-agent-id');
    window.location.reload();
  };

  if (!agent) {
    return (
      <div className="p-8 max-w-xl mx-auto space-y-4 text-center">
        <EmptyState message="Agent record not found. Please log in again." />
        <button
          onClick={handleSignOut}
          className="px-4 py-2 bg-primary-600 text-white font-bold rounded-xl text-xs cursor-pointer"
        >
          Return to Login
        </button>
      </div>
    );
  }

  const completedCoursesCount = agent.courses?.filter((c) => c.completed).length || 0;
  const totalCoursesCount = agent.courses?.length || 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-12">
      {/* Header Bar */}
      <div className="flex items-center justify-between bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary-100 text-primary-700 font-bold text-lg flex items-center justify-center">
            {agent.name.charAt(0)}
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{agent.name}</h1>
            <p className="text-xs font-mono text-slate-500">{agent.pNumber} • {agent.email}</p>
          </div>
        </div>
        <button
          onClick={handleSignOut}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors cursor-pointer"
        >
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5 space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Award className="w-4 h-4 text-primary-600" /> Accreditation Status
          </div>
          <div className="text-lg font-bold text-slate-800">
            {agent.assessments?.callAccreditation?.finalStatus || 'Pending'}
          </div>
        </Card>

        <Card className="p-5 space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <BookMarked className="w-4 h-4 text-emerald-600" /> Compliance Courses
          </div>
          <div className="text-lg font-bold text-slate-800">
            {completedCoursesCount} / {totalCoursesCount} Completed
          </div>
        </Card>

        <Card className="p-5 space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <CheckSquare className="w-4 h-4 text-blue-600" /> Work Pattern
          </div>
          <div className="text-lg font-bold text-slate-800">
            {agent.workPattern || 'Hybrid'} ({agent.contractType || 'Full-time'})
          </div>
        </Card>
      </div>

      {/* Weekly RAG Feedback Notes */}
      <Card className="p-6 space-y-4">
        <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary-600" /> Trainer Weekly Evaluations & Notes
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {agent.weeklyEvaluations?.map((we) => (
            <div key={we.weekNumber} className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700">Week {we.weekNumber}</span>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                    we.calculatedRAG === 'Green'
                      ? 'bg-emerald-100 text-emerald-800'
                      : we.calculatedRAG === 'Amber'
                      ? 'bg-amber-100 text-amber-800'
                      : we.calculatedRAG === 'Red'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {we.calculatedRAG}
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed italic">
                {we.notes ? `"${we.notes}"` : 'No trainer notes logged yet for this week.'}
              </p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
